import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import ConfettiToast from '../../ui/confetti-toast';

const ToastContext = createContext(null);

function ToastProvider({children}) {
  const nextId = useRef(0);
  const [queue, setQueue] = useState([]);

  const [current] = queue;

  /**
   * Queues a toast and returns its id so it can be dismissed programmatically.
   */
  const showToast = useCallback(({title, content, autoHideDuration}) => {
    const id = nextId.current + 1;
    // eslint-disable-next-line fp/no-mutation
    nextId.current = id;
    setQueue(toasts => [
      ...toasts,
      {id, title, content, autoHideDuration, open: true},
    ]);
    return id;
  }, []);

  const dismissToast = useCallback(id => {
    setQueue(toasts =>
      toasts[0]?.id === id
        ? // Let the visible toast animate out before it leaves the queue.
          toasts.map(toast =>
            toast.id === id ? {...toast, open: false} : toast
          )
        : toasts.filter(toast => toast.id !== id)
    );
  }, []);

  const handleExited = useCallback(id => {
    setQueue(toasts => toasts.filter(toast => toast.id !== id));
  }, []);

  const value = useMemo(
    () => ({showToast, dismissToast}),
    [showToast, dismissToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {current && (
        <ConfettiToast
          key={current.id}
          open={current.open}
          title={current.title}
          autoHideDuration={current.autoHideDuration}
          onClose={() => dismissToast(current.id)}
          onExited={() => handleExited(current.id)}>
          {current.content}
        </ConfettiToast>
      )}
    </ToastContext.Provider>
  );
}

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider.');
  }

  return context;
}

export default ToastProvider;
